import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { projectAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowRightLeft, Loader2, AlertTriangle } from 'lucide-react';

export default function TransferOwnership({ projectId, projectName, currentOwner, onTransferred }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [transferring, setTransferring] = useState(false);

  const isAdmin = user?.is_admin || user?.email?.toLowerCase() === 'pm@artisanguildinfra.com';

  if (!isAdmin) {
    return null;
  }

  const handleTransfer = async () => {
    if (!newOwnerEmail.trim()) {
      toast.error('Please enter the new owner email');
      return;
    }

    setTransferring(true);
    try {
      const res = await projectAPI.transferOwnership(projectId, newOwnerEmail);
      toast.success(`Project transferred to ${res.data.new_owner.name || res.data.new_owner.email}`);
      setOpen(false);
      setConfirmOpen(false);
      setNewOwnerEmail('');
      if (onTransferred) {
        onTransferred(res.data);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to transfer ownership');
    } finally {
      setTransferring(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="text-purple-600 border-purple-200 hover:bg-purple-50"
          data-testid="transfer-ownership-btn"
        >
          <ArrowRightLeft className="w-4 h-4 mr-2" />
          Transfer Ownership
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-purple-600" />
            Transfer Project Ownership
          </DialogTitle>
          <DialogDescription>
            Transfer "{projectName}" to another user
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">This action cannot be undone</p>
              <p className="mt-1">The new owner will have full control over this project. The current owner will be demoted to a team member.</p>
            </div>
          </div>

          <div>
            <Label className="text-sm text-zinc-600">Current Owner</Label>
            <p className="font-medium text-zinc-900">{currentOwner?.name || currentOwner?.email || 'Unknown'}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-owner-email">New Owner Email</Label>
            <Input
              id="new-owner-email"
              type="email"
              placeholder="newowner@example.com"
              value={newOwnerEmail}
              onChange={(e) => setNewOwnerEmail(e.target.value)}
              data-testid="new-owner-email-input"
              className="rounded-xl"
            />
            <p className="text-xs text-zinc-500">Enter the email of an existing user</p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!newOwnerEmail.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
            >
              Continue
            </Button>
          </div>
        </div>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Transfer</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to transfer "{projectName}" to <strong>{newOwnerEmail}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={transferring}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleTransfer}
                disabled={transferring}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {transferring ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Transfer Ownership
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
